import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CloseIcon from '@mui/icons-material/Close';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FeedbackIcon from '@mui/icons-material/Feedback';
import HomeIcon from '@mui/icons-material/Home';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PaymentIcon from '@mui/icons-material/Payment';
import PeopleIcon from '@mui/icons-material/People';
import SchoolIcon from '@mui/icons-material/School';
import LiveTvRoundedIcon from '@mui/icons-material/LiveTvRounded';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { Link, useLocation } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import GlobalSearch from './GlobalSearch';
import NotificationCenter from './NotificationCenter';
import useMemberAccess from '../../hooks/useMemberAccess';
import './navbar.css';

const mainLinks = [
  { label: 'Learn', path: '/', icon: <HomeIcon /> },
  { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
  { label: 'Courses', path: '/courses', icon: <AutoStoriesIcon /> },
  { label: 'Classes', path: '/classes', icon: <SchoolIcon /> },
  { label: 'Challenges', path: '/challenges', icon: <EmojiEventsIcon /> },
  { label: 'Students', path: '/students', icon: <PeopleIcon /> },
  { label: 'Shows', path: '/shows', icon: <LiveTvRoundedIcon /> },
];

const extraLinks = [
  { label: 'Free courses', path: '/free/courses', icon: <AutoStoriesIcon /> },
  { label: 'Pricing', path: '/pricing', icon: <PaymentIcon /> },
  { label: 'Shop', path: '/shop', icon: <ShoppingCartIcon /> },
  { label: 'Feedback', path: '/feedback', icon: <FeedbackIcon /> },
];

export default function Navbar() {
  const { user, signOut } = useAuthenticator((context) => [context.user]);
  const access = useMemberAccess();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const isAdmin = (user?.signInUserSession?.idToken?.payload?.['cognito:groups'] || []).includes('Admin');
  const isMember = access.hasPremiumAccess;
  const memberLabel = access.source === 'complimentary' ? 'COMPLIMENTARY' : isMember ? 'GOLD MEMBER' : 'EXPLORER';
  const displayName = user?.attributes?.name || user?.attributes?.given_name || user?.username || 'Dancer';
  const avatarLetter = String(displayName).trim().charAt(0).toUpperCase() || 'J';
  useEffect(() => {
    const updateScrolled = () => setScrolled(window.scrollY > 8);
    updateScrolled();
    window.addEventListener('scroll', updateScrolled, { passive: true });
    return () => window.removeEventListener('scroll', updateScrolled);
  }, []);
  const isActive = (path) => {
    if (path === '/') return location.pathname === '/' || location.pathname === '/Welcome';
    if (path === '/admin') return location.pathname === '/admin';
    if (path === '/classes') return location.pathname.startsWith('/classes') || location.pathname.startsWith('/lessons/');
    return location.pathname.startsWith(path);
  };

  const drawerLink = (item) => (
    <ListItemButton component={Link} to={item.path} onClick={() => setOpen(false)} aria-current={isActive(item.path) ? 'page' : undefined} className={isActive(item.path) ? 'is-active' : ''} key={item.path}>
      <ListItemIcon>{item.icon}</ListItemIcon>
      <ListItemText primary={item.label} />
    </ListItemButton>
  );

  return (
    <>
      <AppBar position="sticky" className={`dance-nav ${scrolled ? 'is-scrolled' : ''}`} elevation={0}>
        <Toolbar className="dance-nav-toolbar">
          <Stack component={Link} to="/" direction="row" alignItems="center" spacing={1.2} className="dance-nav-brand">
            <Box className="dance-nav-mark">JL</Box>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography className="dance-nav-name">JA LEGENDS</Typography>
              <Typography className="dance-nav-caption">DANCE ACADEMY</Typography>
            </Box>
          </Stack>

          <Stack direction="row" className="dance-nav-links">
            {mainLinks.map((item) => (
              <Button component={Link} to={item.path} startIcon={item.icon} aria-current={isActive(item.path) ? 'page' : undefined} className={`dance-nav-link ${isActive(item.path) ? 'is-active' : ''}`} key={item.path}>
                {item.label}
              </Button>
            ))}
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1} ml="auto">
            <GlobalSearch />
            <NotificationCenter />
            <Tooltip title={isMember ? 'Open your dashboard' : 'View membership plans'} arrow>
              <Button component={Link} to={isMember ? '/dashboard' : '/pricing'} className="dance-nav-member" startIcon={isMember ? <DashboardIcon /> : <PaymentIcon />}>{memberLabel}</Button>
            </Tooltip>
            {isAdmin && <Tooltip title="Admin dashboard" arrow><IconButton component={Link} to="/admin" className="dance-nav-admin" aria-label="Open admin dashboard"><AdminPanelSettingsIcon /></IconButton></Tooltip>}
            <Tooltip title="More navigation" arrow><IconButton className="dance-nav-more" aria-label="Open more navigation" onClick={() => setOpen(true)}><MoreHorizIcon /></IconButton></Tooltip>
            <Button className="dance-nav-signout" startIcon={<LogoutIcon />} onClick={signOut}>Sign out</Button>
            <IconButton className="dance-nav-mobile-menu" aria-label="Open navigation" onClick={() => setOpen(true)}><MenuIcon /></IconButton>
          </Stack>
        </Toolbar>
      </AppBar>

      <Drawer anchor="right" open={open} onClose={() => setOpen(false)} PaperProps={{ className: 'dance-nav-drawer' }}>
        <Box className="dance-nav-drawer-head">
          <Avatar className="dance-nav-avatar">{avatarLetter}</Avatar>
          <Box minWidth={0} flex={1}><Typography fontWeight={950} noWrap>{displayName}</Typography><Typography variant="caption">Keep moving. Keep growing.</Typography></Box>
          <IconButton aria-label="Close navigation" onClick={() => setOpen(false)}><CloseIcon /></IconButton>
        </Box>
        <Box className="dance-nav-drawer-status"><Chip size="small" className="dance-nav-member" label={isMember ? 'GOLD MEMBER' : 'EXPLORER'} /><Button component={Link} to="/dashboard" onClick={() => setOpen(false)} endIcon={<DashboardIcon />}>Open dashboard</Button></Box>
        <Divider />
        <Typography variant="overline" className="dance-nav-drawer-label">YOUR DANCE JOURNEY</Typography>
        <List className="dance-nav-drawer-list">{mainLinks.map(drawerLink)}</List>
        <Divider />
        <Typography variant="overline" className="dance-nav-drawer-label">MORE FROM JA LEGENDS</Typography>
        <List className="dance-nav-drawer-list">{extraLinks.map(drawerLink)}</List>
        {isAdmin && <><Divider /><Typography variant="overline" className="dance-nav-drawer-label">ADMIN TOOLS</Typography><List className="dance-nav-drawer-list">
          {drawerLink({ label: 'Admin Home', path: '/admin', icon: <AdminPanelSettingsIcon /> })}
          {drawerLink({ label: 'Content Studio', path: '/admin/content', icon: <AdminPanelSettingsIcon /> })}
          {drawerLink({ label: 'Shows Studio', path: '/admin/shows', icon: <LiveTvRoundedIcon /> })}
          {drawerLink({ label: 'Journey Lessons & Quizzes', path: '/admin/journey-lessons', icon: <AutoStoriesIcon /> })}
          {drawerLink({ label: 'Users & Analytics', path: '/admin/analytics', icon: <PeopleIcon /> })}
        </List></>}
        <Box className="dance-nav-drawer-footer">
          <Button fullWidth variant="outlined" startIcon={<PeopleIcon />} href="https://discord.gg/spyQYAC4Wh">Community</Button>
          <Button fullWidth color="error" startIcon={<LogoutIcon />} onClick={signOut}>Sign out</Button>
        </Box>
      </Drawer>
    </>
  );
}
